import { useQuery } from "@apollo/client";
import { FC, useState } from "react";
import styled from "styled-components";
import { GET_ITEMS_ADMIN } from "../../query/ItemQueries";
import { GET_ME } from "../../query/UserQueries";
import { ADMIN_USERNAME } from "../Chart/Charts";
import { StyledChartContainer, StyledSection } from "../Chart/common/styles";
import Error from "../Error/QueryError";
import IconItem from "../Icon/IconItem";
import Loading from "../Loading";
import { H1, H2 } from "../Title";

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ItemCard = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 0.75rem;
  border: 1px solid #444;
  color: white;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const ItemIconWrapper = styled.div`
  flex: 3;
  min-width: 0;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemDetails = styled.div`
  font-size: 0.8rem;
  color: #ccc;

  & > div {
    margin-bottom: 0.15rem;
  }

  & strong {
    color: white;
  }
`;

const StatsSection = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #555;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
`;

const StatLabel = styled.span`
  color: #ccc;
`;

const StatValue = styled.span`
  font-weight: bold;
  color: #4caf50;
`;

const NewItemBadge = styled.div`
  background: #ff9800;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-weight: bold;
  margin-left: 0.5rem;
`;

const ToggleButton = styled.button`
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background-color 0.2s;

  &:hover {
    background: #1976d2;
  }
`;

const NewspaperVersions = styled.div`
  margin-top: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  padding: 0.5rem;
  border: 1px solid #555;
`;

const NewspaperVersion = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #ddd;
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  & span:first-child {
    color: #ffd700;
    font-weight: bold;
  }
`;

type ItemsAdminProps = {};

export const ItemsAdmin: FC<ItemsAdminProps> = () => {
  const [expandedNewspapers, setExpandedNewspapers] = useState<Set<string>>(
    new Set()
  );

  const { loading: loadingMe, error: errorMe, data: dataMe } = useQuery(GET_ME);

  const {
    loading: loadingItems,
    error: errorItems,
    data: dataItems,
  } = useQuery(GET_ITEMS_ADMIN);

  const toggleNewspaperVersions = (itemId: string) => {
    setExpandedNewspapers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const isLoading = loadingMe || loadingItems;
  const isError = errorMe || errorItems;

  if (isLoading) return <Loading />;
  if (isError) return <Error error={errorMe || errorItems} />;

  const currentUser = dataMe?.me;
  if (currentUser?.username !== ADMIN_USERNAME) {
    return (
      <StyledChartContainer>
        <StyledSection>
          <h1>Accès non autorisé</h1>
          <p>Vous n'avez pas la permission de voir cette page.</p>
        </StyledSection>
      </StyledChartContainer>
    );
  }

  const { availableItems, itemStats } = dataItems?.itemsAdmin || {};

  if (!availableItems || !itemStats) {
    return (
      <StyledChartContainer>
        <StyledSection>
          <h1>Erreur</h1>
          <p>Impossible de charger les données des items.</p>
        </StyledSection>
      </StyledChartContainer>
    );
  }

  // Créer un map des stats par itemId pour un accès rapide
  const statsMap = new Map();
  itemStats.forEach((stat: any) => {
    statsMap.set(stat.itemId, stat);
  });

  return (
    <StyledChartContainer>
      <H1>Administration des Items</H1>

      <H2>Items Disponibles ({availableItems.length})</H2>

      <ItemsGrid>
        {availableItems.map((item: any) => {
          const stats = statsMap.get(item.id);
          const hasNewspaperVersions =
            stats?.newspaperVersions && stats.newspaperVersions.length > 0;
          const isExpanded = expandedNewspapers.has(item.id);

          return (
            <ItemCard key={item.id}>
              <ItemHeader>
                <ItemIconWrapper>
                  <IconItem playerItem={null} item={item} />
                </ItemIconWrapper>
                <ItemInfo>
                  <ItemDetails>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <strong>ID:</strong> {item.id}
                      {stats && stats.totalPlayers === 0 && (
                        <NewItemBadge>NOUVEAU</NewItemBadge>
                      )}
                    </div>
                    <div>
                      <strong>Type:</strong> {item.type}
                    </div>
                    <div>
                      <strong>Rareté:</strong> {item.rarity}
                    </div>
                    <div>
                      <strong>Prix:</strong> {item.price} PO
                    </div>
                    <div>
                      <strong>Quantité max:</strong> {item.maxQuantity}
                    </div>
                  </ItemDetails>
                </ItemInfo>
              </ItemHeader>

              {stats && (
                <StatsSection>
                  <StatRow>
                    <StatLabel>Joueurs possédant cet item:</StatLabel>
                    <StatValue>{stats.totalPlayers}</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Quantité totale en circulation:</StatLabel>
                    <StatValue>{stats.totalOwned}</StatValue>
                  </StatRow>

                  {hasNewspaperVersions && (
                    <>
                      <ToggleButton
                        onClick={() => toggleNewspaperVersions(item.id)}
                      >
                        {isExpanded ? "Masquer" : "Voir"} les versions de
                        journaux ({stats.newspaperVersions.length})
                      </ToggleButton>

                      {isExpanded && (
                        <NewspaperVersions>
                          <StatLabel
                            style={{
                              marginBottom: "0.5rem",
                              display: "block",
                              color: "#fff",
                            }}
                          >
                            Versions de journaux:
                          </StatLabel>
                          {stats.newspaperVersions.map((version: any) => (
                            <NewspaperVersion key={version.version}>
                              <span>#{version.version}</span>
                              <span>{version.count} exemplaires</span>
                            </NewspaperVersion>
                          ))}
                        </NewspaperVersions>
                      )}
                    </>
                  )}
                </StatsSection>
              )}
            </ItemCard>
          );
        })}
      </ItemsGrid>
    </StyledChartContainer>
  );
};

export default ItemsAdmin;
